import { useCallback } from 'react'

import { setAnalyticsEvent } from '../../../analytics/state'
import { useCartContext } from '../../../cart'
import { eScript20230411Version } from '../../../escript/config'
import { useEScriptParamsLoader } from '../../../escript/hooks/useEScriptParamsLoader'
import {
  clearEScriptReports,
  invalidCouponCodeError,
  setEScriptErrors,
} from '../../../escript/state'
import { EScriptParamsLoader } from '../../../escript/types'
import { resetDispensaryCartState, setDispensaryCartState } from '../../events/cart'
import { setEScriptCartUpdateStatus } from '../../state'
import { useDispensaryContext } from '../useDispensaryContext'
import { useAddProductsToCart } from './useAddProductsToCart'
import { isIgnorablePromoCodeError, useApplyPromoCodeToCart } from './useApplyPromoCodeToCart'
import { useEmptyCart } from './useEmptyCart'
import { useRemoveDispensaryPromoCodeFromCart } from './useRemoveDispensaryPromoCodeFromCart'
import { useSetDispensaryPromoCodeOnCart } from './useSetDispensaryPromoCodeOnCart'
import { useUpdateCartAttributes } from './useUpdateCartAttributes'

export type UseDispensaryUpdateCart = [updateCartDispensary: UpdateCartDispensary]

export type UpdateCartDispensary = (options: {
  forceClearEScriptParams: boolean
  shouldClearDispensaryPromoCode: boolean
}) => Promise<void>

const publishAddEscriptToCartEvent = async (
  eScriptParamsLoader: EScriptParamsLoader,
): Promise<void> => {
  const { loadParams } = eScriptParamsLoader
  const [{ version, cartAttributes, link }] = await Promise.all([loadParams()])

  if (version === 'well_world' && cartAttributes?.type === 'wellWorld' && link !== undefined) {
    setAnalyticsEvent({
      event: 'add_escript_to_cart',
      escript_version: version,
      escript_campaign: 'well_world',
      escript_link: link,
    })
  }

  if (
    version === eScript20230411Version &&
    cartAttributes?.type === 'eScriptCampaign' &&
    link !== undefined
  ) {
    setAnalyticsEvent({
      event: 'add_escript_to_cart',
      escript_version: version,
      escript_campaign: cartAttributes?.attributes.eScriptCampaign,
      escript_link: link,
    })
  }
}

export const useDispensaryUpdateCart = (): UseDispensaryUpdateCart => {
  const dispensaryContext = useDispensaryContext()
  const cartContext = useCartContext()
  const eScriptParamsLoader = useEScriptParamsLoader()

  const [emptyCart] = useEmptyCart({ eScriptParamsLoader })
  const [addProductsToCart] = useAddProductsToCart({ eScriptParamsLoader })
  const [removeDispensaryPromoCodeFromCart] = useRemoveDispensaryPromoCodeFromCart({ cartContext })
  const [applyPromoCodeToCart] = useApplyPromoCodeToCart({ eScriptParamsLoader })
  const [setDispensaryCodeOnCart] = useSetDispensaryPromoCodeOnCart({ eScriptParamsLoader })
  const [updateCartAttributes] = useUpdateCartAttributes({ dispensaryContext, eScriptParamsLoader })

  const { clearParams, loadParams } = eScriptParamsLoader

  const updateCartDispensary = useCallback<UpdateCartDispensary>(
    async ({ forceClearEScriptParams, shouldClearDispensaryPromoCode }) => {
      try {
        // report status so other logic (e.g. global promotions) can collaborate on cart updates
        setEScriptCartUpdateStatus('in_progress')
        // both hold their values for the lifetime of the page, so a new hydration has to start from
        // a clean slate rather than inherit the previous cart's reports and terminal state
        clearEScriptReports()

        // Whether this hydration replaces the cart's items, which is the only part of it the shopper
        // must not watch happen. Every step below stays silent when it has no work, so a hydration
        // that only reconciles cart attributes publishes nothing and the loaded cart renders
        // straight away — announcing unconditionally took that from every dispensary entry rather
        // than the eScript ones, and `dispensaryCartLoading` also disables the add-to-cart controls
        // in `ProtocolAddToCart`, `ItemControls`, `ReorderAll` and `OrderItemEntry`.
        //
        // Reading the params costs no round trip: `loadParams` resolves off the render that parsed
        // the URL, and the span worth covering is what `useEmptyCart` awaits after it — the settled
        // cart, then the removals themselves.
        const { updateCartActions } = await loadParams()

        // One publication either way, never a reset followed by an announce. React 17 does not batch
        // external-store updates, so a `cart_initial` frame between the two is renderable, and over
        // an empty cart it is the frame `NonEmptyCartContextProvider` reads as the recovery page —
        // firing its `navigate('/checkout', { replace: true })` effect, which would take the eScript
        // link out of the address bar. `cart_hydration_start` is itself a head, so it clears the
        // previous hydration the way the reset would have.
        if (updateCartActions?.length) {
          setDispensaryCartState({ type: 'cart_hydration_start' })
        } else {
          resetDispensaryCartState()
        }
        await emptyCart()
        const addResult = await addProductsToCart()
        // An eScript link whose every product failed to add leaves the cart empty, and Magento
        // refuses both coupon mutations on an itemless quote. Measured against dev Magento:
        // `applyCouponToCart` answers a bare "Internal server error", `removeCouponFromCart` answers
        // "Cart does not contain products". Neither is an ignorable promo-code error, so either would
        // reject the whole hydration and hand the shopper the generic cart-error page — taking the
        // dropped-items report down with it, in the very case the shopper most needs it (sc-137931).
        //
        // Nothing added means an empty cart rather than merely an unchanged one: every link version
        // emits `empty` and `add` as a pair (`useEScript20230411ParamsLoader`,
        // `useWellWorldParamsLoader`), so the prior contents are already gone by here. A link that
        // requested no add at all resolves `undefined` and is left alone — its cart may legitimately
        // hold items, and that code still has something to discount.
        //
        // The two coupon steps are handled differently because only one of them has work to do here.
        // The apply is skipped outright: an empty cart has nothing to discount. The removal is still
        // attempted, since a root promo left standing would discount whatever the shopper adds by
        // hand — but a failure is tolerated rather than rethrown, so the report survives either
        // answer. `useLogAndCaptureError` on the mutation still reports it.
        //
        // The removal only reaches Magento at all off a stale read: emptying the cart drops
        // `applied_coupons` on Magento's side, but `removeItemFromCartLite` selects `__typename`
        // alone, so nothing refreshes the cached cart that `cartCoupons` then reads for coupons to
        // remove. Hence tolerating rather than skipping — the call is worth making, and the answer to
        // it is not worth the shopper's report.
        //
        // `setDispensaryCodeOnCart` is left alone: `setDispensaryPromoCodeOnCart` writes a cart
        // attribute rather than a coupon, so it is not on the surface that rejects an itemless
        // quote — and it is what attributes the cart to the practitioner, so skipping it would cost
        // a shopper who then adds products by hand that attribution.
        const addLeftCartEmpty = addResult !== undefined && addResult.itemsAdded.length === 0

        await removeDispensaryPromoCodeFromCart({ shouldClearDispensaryPromoCode }).catch(
          (err: unknown) => {
            if (isIgnorablePromoCodeError(err) || addLeftCartEmpty) {
              console.warn(err)
              return
            }
            throw err
          },
        )
        if (!addLeftCartEmpty) {
          await applyPromoCodeToCart().catch((err: unknown) => {
            if (isIgnorablePromoCodeError(err)) {
              const invalidCouponCodeErrorMessage = invalidCouponCodeError(err)
              if (invalidCouponCodeErrorMessage) {
                setEScriptErrors([
                  { _tag: 'InvalidPromoCode', error_message: invalidCouponCodeErrorMessage },
                ])
              }
              console.warn(err)
              return
            }
            throw err
          })
        }
        await updateCartAttributes({
          forceClearEScriptParams,
          shouldClearDispensaryPromoCode,
        })
        await setDispensaryCodeOnCart()
        await publishAddEscriptToCartEvent(eScriptParamsLoader)
        setDispensaryCartState({ type: 'cart_final' })
        clearParams()
      } catch (error) {
        // Whatever the hydration published, it has to publish how it ended. Leaving the state on a
        // `_start` step reports loading forever, which holds `NonEmptyCartContextProvider` on the
        // checkout skeleton indefinitely.
        //
        // `cart_error` rather than `cart_final`: a failure after `emptyCart` leaves an empty cart, so
        // `cart_final` reads as a finished hydration and the provider renders the empty-cart recovery
        // page for the render before the rethrow reaches `useDispensaryUpdateSession` and swaps in
        // `DispensaryUpdateCartErrorPage`. The provider checks its error branch first, so reporting
        // the failure goes straight to error UI without that flash. Capture belongs to the caller.
        setDispensaryCartState({
          type: 'cart_error',
          error: error instanceof Error ? error : new Error(`${error}`),
        })
        throw error
      } finally {
        setEScriptCartUpdateStatus('idle')
      }
    },
    [
      emptyCart,
      addProductsToCart,
      removeDispensaryPromoCodeFromCart,
      applyPromoCodeToCart,
      updateCartAttributes,
      setDispensaryCodeOnCart,
      eScriptParamsLoader,
      clearParams,
      loadParams,
    ],
  )

  return [updateCartDispensary]
}
